import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { Unsubscriber } from '../../unsubscriber/unsubscriber.component';
import { ServiceOrder } from '../models/service-order.model';

/** O que o alerts-api grava no close: detalhes (close_notes) e a flag de pendência (with_issues) **/
export interface FinishServiceOrderPayload {
  closeNotes: string;
  withIssues: boolean;
}

/**
 * Dialog "Finalizar OS" (Proposta F): já é a confirmação, sem Swal antes. Uma linha com o label
 * dos detalhes, a tag de regra e o checkbox "Com pendência"; o textarea abaixo. Sem pendência os
 * detalhes são opcionais; com pendência passam a ser obrigatórios e a tag, o placeholder e o
 * botão mudam junto, para a consequência ficar visível sem depender do tooltip.
 */
@Component({
  selector: 'app-service-order-finish-dialog',
  templateUrl: './service-order-finish-dialog.component.html',
  styleUrls: ['./service-order-finish-dialog.component.less'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    ProgressSpinnerModule,
    TextareaModule,
    TooltipModule,
  ],
})
export class ServiceOrderFinishDialogComponent extends Unsubscriber {
  constructor(
    private formBuilder: FormBuilder,
    private changeDetector: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {
    super();

    this.anotherSubscription = this.form.get('withIssues')!.valueChanges.subscribe((withIssues: boolean) => {
      const notes = this.form.get('closeNotes')!;

      if (withIssues) {
        notes.setValidators(Validators.required);
      } else {
        notes.clearValidators();
      }

      notes.updateValueAndValidity({ emitEvent: false });
    });
  }

  @Input({ required: true }) serviceOrder!: ServiceOrder;

  /** Enquanto o close está em voo o rodapé vira spinner e o dialog não fecha **/
  @Input() loading: boolean = false;

  @Output() confirmed = new EventEmitter<FinishServiceOrderPayload>();

  @Output() cancelled = new EventEmitter<void>();

  public visible: boolean = true;

  /** Erro inline só depois de tentar finalizar, não enquanto digita **/
  public submitted: boolean = false;

  public form: FormGroup = this.formBuilder.group({
    withIssues: [false],
    closeNotes: [''],
  });

  public get withIssues(): boolean {
    return !!this.form.get('withIssues')?.value;
  }

  public get showError(): boolean {
    return this.submitted && this.withIssues && !!this.form.get('closeNotes')?.invalid;
  }

  public confirm(): void {
    if (this.loading) {
      return;
    }

    this.submitted = true;

    if (this.form.invalid) {
      this.host.nativeElement.querySelector<HTMLTextAreaElement>('#os-finish-notes')?.focus();
      return;
    }

    this.confirmed.emit({
      closeNotes: (this.form.get('closeNotes')?.value ?? '').trim(),
      withIssues: this.withIssues,
    });
  }

  public cancel(): void {
    if (this.loading) {
      return;
    }

    this.visible = false;
    this.cancelled.emit();
  }

  /**
   * Fechamento disparado pelo próprio p-dialog. O clique na máscara vem de um listener do PrimeNG
   * fora do ciclo do template; markForCheck garante que o pai reavalie o @if do modal.
   */
  public onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.visible = true;
      this.cancel();
      this.changeDetector.markForCheck();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  public onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (this.visible && target?.classList?.contains('p-dialog-mask') && this.host.nativeElement.contains(target)) {
      this.cancel();
    }
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.visible) {
      this.cancel();
    }
  }
}
